import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/job_aspira-land/artifacts/f18d49419b28ef3d_Aspira%20Property%20Group%20Gold%20Logo.png";

const LINKS = [
  { name: "Home", path: "/", testid: "nav-home" },
  { name: "Sell Your Land", path: "/sell", testid: "nav-sell" },
  { name: "Buy Land", path: "/buy", testid: "nav-buy" },
  { name: "About Us", path: "/about", testid: "nav-about" },
  { name: "Contact", path: "/contact", testid: "nav-contact" },
];

export const LogoMark = ({ footer = false }) => (
  <Link to="/" className="inline-flex items-center" data-testid="logo-link" aria-label="Aspira Property Group — home">
    <img
      src={LOGO_URL}
      alt="Aspira Property Group"
      className={`w-auto ${footer ? "h-16 sm:h-[4.5rem]" : "h-14 sm:h-16"}`}
    />
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 border-b border-aspira-goldborder/50 bg-aspira-cream/90 shadow-[0_1px_0_rgba(197,160,89,0.08)] backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <LogoMark />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative rounded-full px-4 py-2 text-[13px] font-medium transition-colors duration-200 ${
                  isActive ? "text-aspira-ink" : "text-aspira-body hover:text-aspira-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.name}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-aspira-gold"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
          <Link
            to="/sell"
            data-testid="nav-cta-offer"
            className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-aspira-gold px-5 py-2.5 text-[13px] font-semibold text-white shadow-md transition-all duration-300 hover:bg-aspira-goldhover hover:shadow-lg"
          >
            Get Cash Offer <ArrowRight size={14} />
          </Link>
        </nav>
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="rounded-lg p-2 text-aspira-ink lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-aspira-goldborder/50 bg-aspira-cream lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  data-testid={`${l.testid}-mobile`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                      isActive ? "bg-aspira-goldlight text-aspira-ink" : "text-aspira-body hover:bg-aspira-sand"
                    }`
                  }
                >
                  {l.name}
                </NavLink>
              ))}
              <Link
                to="/sell"
                data-testid="nav-cta-offer-mobile"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-aspira-gold px-5 py-3 text-sm font-semibold text-white"
              >
                Get Cash Offer <ArrowRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
